import { BodyText, Button, Search, toast } from "@/components/ui";
import type { ItemOptionProps } from "@/components/ui/Search/types";
import { TRACKERS } from "@/constants/Trackers";
import { usePatientDemographics } from "@/features/Patient/PatientBar/network/usePatientDemographics";
import { trackEvent } from "@/lib/tracking";
import { cleanObject } from "@/lib/utils";
import { isActionAvailable } from "@/utils/available-actions";
import { constants, type Condition, getCoding } from "@curai/js-fhir-tools";
import { useParams, useSearch } from "@tanstack/react-router";
import { useAtom } from "jotai";
import { LoaderCircle, RefreshCwIcon, SearchIcon } from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import { useDebounce } from "react-use";
import { useCreatePrescription } from "../../network/useCreatePrescription";
import {
  type FormularyDrugCodings,
  type OrderSentence,
  useFormularySearch,
} from "../../network/useFormularySearch";
import { activeCommandAtom } from "../../state/atoms";
import { useCarePlanSummary } from "../network/useCarePlanSummary";
import { CareplanAvailableAction } from "../network/useCarePlanSummary";
import { useCreateOTCPrescription } from "../network/useCreateOTCPrescription";
import { RenewalModal } from "./Renewals/RenewalModal";
import { useRenewablePrescriptions } from "./network/useRenewablePrescriptions";

interface AddMedicationProps {
  encounterId: string;
  carePlanId: string;
  condition?: Condition;
  conditions?: Condition[];
  otcAllowed: boolean;
}

interface MedicationItemMetadata {
  drugCodes: FormularyDrugCodings[];
  orderSentence: OrderSentence;
}

export const AddMedication = ({
  encounterId,
  carePlanId,
  condition,
  conditions,
  otcAllowed,
}: AddMedicationProps) => {
  const { patientId } = useParams({ from: "/patient/$patientId" });
  const { encounterId: motherEncounterId } = useSearch({
    from: "/patient/$patientId",
  });
  const { data: patientDemographics } = usePatientDemographics(patientId);
  const snomedCode = condition?.code
    ? getCoding(condition.code, constants.SNOMED_SYSTEM_URL)?.code
    : undefined;
  const snomedCodes = conditions?.flatMap((condition) => {
    const code = condition.code
      ? getCoding(condition.code, constants.SNOMED_SYSTEM_URL)?.code
      : undefined;

    return code ? [code] : [];
  });

  // Prepared for future multi-condition formulary support.
  void snomedCodes;

  const [input, setInput] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [activeCommand, setActiveCommand] = useAtom(activeCommandAtom);
  const addButtonRef = useRef<HTMLButtonElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);
  const [isRenewalModalOpen, setIsRenewalModalOpen] = useState(false);

  // Hack to force a re-render of the Search component to clear the input only
  // after the creation of the prescription succeeds. I'll fix this later.
  const [forceClearInput, setForceClearInput] = useState(0);

  const createPrescription = useCreatePrescription({
    patientId,
    encounterId,
  });

  const createOTCPrescription = useCreateOTCPrescription({
    patientId,
    encounterId,
  });

  const { data: rxRenewals } = useRenewablePrescriptions({
    patientId,
    motherEncounterId: motherEncounterId
      ? String(motherEncounterId)
      : undefined,
  });

  useDebounce(() => setSearchTerm(input), 200, [input]);
  const partnerUid = patientDemographics?.membership?.partnerUids[0];

  const { data, isLoading } = useFormularySearch({
    searchTerm,
    partnerUid,
    snomedCode: partnerUid && snomedCode ? snomedCode : undefined,
  });

  const options = useMemo(() => {
    return (data?.items.map((item) => ({
      label: item.medicationName,
      value: item.drugCodes,
      metadata: {
        drugCodes: item.drugCodes,
        orderSentence: item.orderSentence,
      } as MedicationItemMetadata,
    })) || []) as Array<ItemOptionProps>;
  }, [data?.items]);

  const { data: careplanSummary } = useCarePlanSummary(encounterId);
  const careplanAvailableActions = careplanSummary?.availableActions;
  const canAddMedications = isActionAvailable(
    CareplanAvailableAction.ADD_PRESCRIPTION,
    careplanAvailableActions,
  );

  useEffect(() => {
    // This effect specifically handles the "addMedication" command from QuickCommand
    if (
      activeCommand &&
      activeCommand.type === "addMedication" &&
      !activeCommand.handled &&
      (addButtonRef.current || searchRef.current)
    ) {
      let claimed = false;
      setActiveCommand((currentCmd) => {
        // Check if it's the same unhandled command we saw
        if (
          currentCmd &&
          currentCmd.id === activeCommand.id &&
          currentCmd.type === "addMedication" &&
          !currentCmd.handled
        ) {
          claimed = true; // Mark that this instance will handle it
          return { ...currentCmd, handled: true }; // Update atom to mark as handled
        }
        return currentCmd; // Otherwise, no change to the atom
      });

      if (claimed && (addButtonRef.current || searchRef.current)) {
        // If this instance successfully claimed the command
        // The Search component has autoFocusInput={true}, so it should focus its input after this click
        if (searchRef.current) {
          searchRef.current.focus();
        } else if (addButtonRef.current) {
          addButtonRef.current.click();
        }
        // Reset the active command atom completely after handling
        // We do this in a microtask to ensure the click processing and subsequent UI updates (like Search appearing) can occur.
        queueMicrotask(() => {
          setActiveCommand(null);
        });
      }
    }
  }, [activeCommand, setActiveCommand]);

  const onSubmit = async (selectedItem: ItemOptionProps) => {
    try {
      if (selectedItem.isFreeText) {
        await createOTCPrescription.mutateAsync({
          patientId,
          encounterId,
          carePlanId,
          freetextMedicationDetails: selectedItem.label,
        });
      } else {
        const { drugCodes, orderSentence } =
          selectedItem.metadata as MedicationItemMetadata;
        await createPrescription.mutateAsync({
          patientId,
          encounterId,
          carePlanId,
          medication: {
            coding:
              drugCodes?.map((coding) => ({
                system: coding.codeSystem,
                code: coding.code,
                display: selectedItem.label,
              })) || [],
            text: selectedItem.label,
          },
          prescription: cleanObject({
            dispense: cleanObject({
              daysSupply: orderSentence?.daysSupply,
              quantity: orderSentence?.dispenseQuantity,
              unit: orderSentence?.dispenseQuantityUnit,
              refills: orderSentence?.refills,
            }),
            sig: orderSentence?.directions,
            noteToPharmacy: orderSentence?.noteToPharmacy,
            substitutionAllowed: orderSentence?.substitutionAllowed,
          }),
        });
        setForceClearInput(forceClearInput + 1);
        setInput("");
        setSearchTerm("");
      }
    } catch (error) {
      toast.error("Failed to add prescription");
    }
  };

  return (
    <div className="flex flex-row gap-2 items-center">
      <Search
        key={forceClearInput}
        ref={searchRef}
        placeholder="Search for medications"
        options={
          options?.length > 0
            ? options
            : [
                {
                  label: "No results found",
                  value: "No results found",
                },
              ]
        }
        isLoading={isLoading || createPrescription.isPending}
        disabled={createPrescription.isPending || !canAddMedications}
        onInputChange={setInput}
        onItemSelected={(item) => {
          onSubmit(item);
        }}
        allowFreeText={otcAllowed}
        freeTextPrefix="Add as OTC: "
        noResultsText=""
        leftIcon={createPrescription.isPending ? LoaderCircle : SearchIcon}
        openOnFocus={true}
        openWithoutInput={true}
      />
      {Boolean(rxRenewals?.prescriptionsForDrugs?.length) &&
        Boolean(motherEncounterId) && (
          <>
            <BodyText size="sm">or</BodyText>
            <Button
              size="sm"
              variant="warning"
              className="w-fit"
              disabled={!canAddMedications}
              leftIcon={RefreshCwIcon}
              onClick={() => {
                trackEvent(TRACKERS.VIEW_ELIGIBLE_RENEWALS);
                setIsRenewalModalOpen(true);
              }}
            >
              Renewal
            </Button>
            {isRenewalModalOpen && (
              <RenewalModal
                open={isRenewalModalOpen}
                onOpenChange={setIsRenewalModalOpen}
                eocName={condition?.displayName || ""}
                patientId={patientId}
                encounterId={encounterId}
                motherEncounterId={motherEncounterId as string}
              />
            )}
          </>
        )}
    </div>
  );
};
